import {
	Bar,
	BarChart,
	CartesianGrid,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { ChartCard } from "./chart-card";
import { BAR_VALUE_LABEL, NO_ANIMATION } from "./chart-ticks";
import { COLORS } from "./utils";
import type { GraduationYearStatistics } from "@/lib/statistics-api.service";

interface GraduationYearChartProps {
	/** `undefined` = lần tải đầu chưa xong. */
	rows: GraduationYearStatistics[] | undefined;
}

/**
 * Biểu đồ cột theo năm tốt nghiệp THPT: đã tiếp nhận / chưa tiếp nhận.
 *
 * `enrolled`/`notEnrolled` KHÔNG được vẽ thêm: cả hai trùng giá trị với
 * `received`/`pending` (server suy "đã nhập học" từ cùng cờ `DaTiepNhan`, không
 * có cột nhập học riêng) — vẽ đủ bốn cột sẽ trông như bốn đại lượng khác nhau
 * trong khi thực ra chỉ có hai.
 */
export function GraduationYearChart({ rows }: GraduationYearChartProps) {
	if (rows === undefined) {
		return (
			<ChartCard title="Thống kê theo năm tốt nghiệp THPT" filename="thong-ke-theo-nam-tot-nghiep">
				<Skeleton className="h-[360px] w-full" />
			</ChartCard>
		);
	}

	if (rows.length === 0) {
		return (
			<ChartCard title="Thống kê theo năm tốt nghiệp THPT" filename="thong-ke-theo-nam-tot-nghiep">
				<p className="py-8 text-center text-sm text-muted-foreground">
					Chưa có dữ liệu năm tốt nghiệp trong phạm vi đang lọc.
				</p>
			</ChartCard>
		);
	}

	const data = [...rows]
		.sort((a, b) => a.graduationYear - b.graduationYear)
		.map((row) => ({
			graduationYear: String(row.graduationYear),
			"Đã tiếp nhận": row.received,
			"Chưa tiếp nhận": row.pending,
		}));

	return (
		<ChartCard title="Thống kê theo năm tốt nghiệp THPT" filename="thong-ke-theo-nam-tot-nghiep">
			<ResponsiveContainer width="100%" height={360}>
				<BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
					<CartesianGrid strokeDasharray="3 3" />
					<XAxis dataKey="graduationYear" />
					<YAxis allowDecimals={false} />
					<Tooltip />
					<Legend />
					<Bar {...NO_ANIMATION} dataKey="Đã tiếp nhận" fill={COLORS[1]} label={BAR_VALUE_LABEL} />
					<Bar
						{...NO_ANIMATION}
						dataKey="Chưa tiếp nhận"
						fill={COLORS[3]}
						label={BAR_VALUE_LABEL}
					/>
				</BarChart>
			</ResponsiveContainer>
		</ChartCard>
	);
}
